/**
 * Label + control + error wrapper. Owns the accessibility contract for every text field.
 *
 * THE BUG THIS FIXES: error messages were bare <p> elements with no id, and inputs had no
 * aria-describedby or aria-invalid. A screen reader user tabbing to a field with an error
 * heard the label and nothing about the problem -- a WCAG 3.3.1 failure, and the audit's most
 * important a11y finding after the missing focus ring.
 *
 * Because CustomerDetailsSection maps over CUSTOMER_FIELDS and renders through this wrapper,
 * a new field cannot be added without the wiring.
 *
 * The `children` render prop receives the ids and aria attributes the control must apply, so
 * the association cannot be forgotten or drift from the markup.
 *
 * @param {object} props
 * @param {string} props.id
 * @param {string} props.label
 * @param {boolean} [props.required]
 * @param {string} [props.error]
 * @param {string} [props.hint]
 * @param {Function} props.children  ({id, describedBy, invalid}) => ReactNode
 */
export default function FormField({ id, label, required, error, hint, children }) {
  const errorId = `${id}-error`;
  const hintId = `${id}-hint`;

  // Point at the error when there is one, otherwise the hint. Both would be noise.
  const describedBy = error ? errorId : hint ? hintId : undefined;

  return (
    <div>
      {/*
        Body size, and NOT the uppercase micro-label the mockups draw. They set field labels at
        11px with letter-spacing, which on a payment form puts the smallest type on the page
        directly above the thing the customer has to read to fill it in correctly.

        Title Case rather than sentence case only because these strings come from
        checkoutSchema.js, which this refactor must not touch -- and lowercasing them here in
        code would turn "Website URL" into "Website url".
      */}
      <label htmlFor={id} className="mb-1.5 block text-sm font-medium text-gray-700">
        {label}
        {required && (
          <>
            {' '}
            <span aria-hidden="true" className="text-danger-600">
              *
            </span>
            <span className="sr-only">(required)</span>
          </>
        )}
      </label>

      {children({ id, describedBy, invalid: Boolean(error) })}

      {hint && !error && (
        <p id={hintId} className="mt-1 text-sm text-gray-500">
          {hint}
        </p>
      )}

      {error && (
        // role="alert" so the message is announced when it appears, not only when the field
        // is next focused.
        <p id={errorId} role="alert" className="mt-1 text-sm text-danger-600">
          {error}
        </p>
      )}
    </div>
  );
}
